import React, { useState, useEffect } from "react";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../../../services/auth.service";
import Navbar from "../../../component/includes/navbar";
import Footer from "../../../component/includes/footer";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
    captchaAnswer: "",
  });

  // Rate Limiting
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [remainingTime, setRemainingTime] = useState("0:00");

  const isLocked = lockUntil !== null && lockUntil > Date.now();

  // CAPTCHA State
  const [captcha, setCaptcha] = useState({
    num1: 0,
    num2: 0,
    question: "",
    correctAnswer: 0,
  });

  // Generate CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 12) + 1;
    const num2 = Math.floor(Math.random() * 12) + 1;
    const correctAnswer = num1 + num2;

    setCaptcha({
      num1,
      num2,
      question: `${num1} + ${num2} = ?`,
      correctAnswer,
    });

    // Reset input captcha
    setFormData(prev => ({ ...prev, captchaAnswer: "" }));
  };

  // Inisialisasi CAPTCHA
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Countdown Lock
  useEffect(() => {
    if (!lockUntil) {
      setRemainingTime("0:00");
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      setRemainingTime(`${minutes}:${seconds.toString().padStart(2, "0")}`);

      if (remaining <= 0) {
        setLockUntil(null);
        if (formData.email) {
          localStorage.removeItem("loginLockUntil_" + formData.email);
        }
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lockUntil, formData.email]);

  // Load dari localStorage ketika email berubah
  useEffect(() => {
    if (formData.email) {
      const savedLockUntil = localStorage.getItem("loginLockUntil_" + formData.email);
      if (savedLockUntil) {
        const lockTime = parseInt(savedLockUntil);
        if (lockTime > Date.now()) {
          setLockUntil(lockTime);
        } else {
          setLockUntil(null);
          localStorage.removeItem("loginLockUntil_" + formData.email);
        }
      } else {
        setLockUntil(null);
      }
    } else {
      setLockUntil(null);
    }
  }, [formData.email]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };



  const handleFocus = () => {
    if (error) setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) return;

    // Validasi CAPTCHA
    if (!formData.captchaAnswer || parseInt(formData.captchaAnswer) !== captcha.correctAnswer) {
      setError("Jawaban CAPTCHA salah. Silakan coba lagi.");
      generateCaptcha(); // regenerate setelah salah
      return;
    }

    try {
      setIsLoading(true);

      const response = await authService.login({
        email: formData.email,
        password: formData.password,
      });

      // Reset semua setelah sukses
      setLockUntil(null);
      generateCaptcha();

      if (formData.rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
      }

      const redirectTo = response.role === "admin" ? "/dashboard" : location.state?.from?.pathname || "/";
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      generateCaptcha(); // regenerate CAPTCHA setelah gagal

      if (err.status === 429) {
        let remainingSeconds = 60; // default 1 menit
        if (typeof err.data === "string" && err.data.includes("Retry after")) {
          const match = err.data.match(/Retry after (\d+) seconds/);
          if (match) {
            remainingSeconds = parseInt(match[1]);
          }
        }
        const lockTime = Date.now() + (remainingSeconds * 1000);
        setLockUntil(lockTime);
        localStorage.setItem("loginLockUntil_" + formData.email, lockTime.toString());
        setError(err.message || `Terlalu banyak percobaan. Silakan coba lagi nanti.`);
      } else {
        setError(err.message || "Email atau password salah");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 mt-10">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img src="/assets/logo.webp" alt="Logo" className="mx-auto h-12 w-auto rounded-xl" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Masuk ke Akun Anda
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-md rounded-lg sm:px-10">
            {error && (
              <div className="mb-4 p-3 text-center text-red-600 text-sm bg-red-50 rounded-md">
                {error}
              </div>
            )}

            {location.state?.message && (
              <div className="mb-4 p-3 text-center text-green-600 text-sm bg-green-50 rounded-md">
                {location.state.message}
              </div>
            )}

            {isLocked && (
              <div className="mb-4 p-3 text-center text-amber-600 text-sm bg-amber-50 rounded-md border border-amber-200">
                Akun Anda terkunci sementara.<br />
                Silakan coba lagi dalam <strong>{remainingTime}</strong>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  id="email" name="email" type="email" required
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  disabled={isLoading || isLocked}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="nama@email.com"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <div className="mt-1 relative">
                  <input
                    id="password" name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    disabled={isLoading || isLocked}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading || isLocked}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                  </button>
                </div>
              </div>

              {/* CAPTCHA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verifikasi CAPTCHA
                </label>
                <div className="flex gap-3 items-center bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
                  <span className="font-mono text-lg font-semibold text-gray-700">
                    {captcha.question}
                  </span>
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    disabled={isLoading || isLocked}
                    className="p-2 hover:bg-gray-100 rounded-md transition"
                  >
                    <RefreshCw className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
                <input
                  type="number"
                  name="captchaAnswer"
                  value={formData.captchaAnswer}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  disabled={isLoading || isLocked}
                  required
                  placeholder="Jawaban"
                  className="mt-2 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={isLoading || isLocked}
                    className="h-4 w-4 text-blue-700"
                  />
                  <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-900">Ingat saya</label>
                </div>
                <a href="/forgot-password" className="text-sm font-medium text-blue-700 hover:text-blue-800">
                  Lupa password?
                </a>
              </div>

              <button
                type="submit"
                disabled={isLoading || isLocked}
                className="w-full py-2.5 px-4 bg-blue-700 hover:bg-blue-800 disabled:bg-gray-400 text-white rounded-full font-medium transition"
              >
                {isLoading ? "Memproses..." : isLocked ? `Terkunci (${remainingTime})` : "Masuk"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}